import Button from "@/common/elements/Button";
import DataTable from "@/common/elements/DataTable";
import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import { PlusOutlined } from "@ant-design/icons";
import DocumentsDetailsModal from "./DocumentsDetailsModal";
import { useMemo, useState } from "react";
import Modal from "@/common/elements/Modal";
import { getFinalPath, hasCheckSecurity, isNonEmptyVariable } from "@/util/common_function";
import { Arbitrary_Names, Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { DOCUMENTS_BASE_URL, PHOTOS_BASE_URL } from "@/util/api";
import notificationService from "@/util/notificationService";
import api from "@/services/axios_custom";
import useDialog from "@/hooks/useDialog";

// Generic linked-documents/scanned-photos widget — the caller owns the data
// (documents/onChange) so it can be backed by Redux (Warrants' activeWarrant)
// or local component state (Transport's in-progress record) alike.
const LinkedScannedDocuments = ({
    documents = [],
    onChange,
    bookingsFieldsEnabled,
    idFieldName = "warrantLinkedDocumentID",
    parentIdFieldName = "warrantID",
    parentId,
    isSearchMode = false,
}) => {
    const { showDialog } = useDialog();
    const [isConfirmModal,setIsConfirmModal] = useState(false);
    const [documentMode,setDocumentMode] = useState("new");
    const [isLinkedDocument,setIsLinkedDocument] = useState(false);
    const [isScannedPhoto,setIsScannedPhoto] = useState(false);
    const [currentSelectedRow,setCurrentSelectedRow] = useState(null);

    const [showModal,setShowModal] = useState(false);

    const linkedDocuments = useMemo(() => documents ?? [], [documents]);
    const [editingIndex, setEditingIndex] = useState(null);

    // Legacy DocumentListInterfaceHelper.asp: "rrh 8/25/2008 crn 1250 only
    // supervisors can delete linked documents" - CanIPerformArbitraryAction
    // ("LinkedDocumentsSuper", ...). Non-supervisors get no Break Link button.
    const isLinkedDocumentsSuper = hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.arbitary,
        LABELS_CAPITAL.VIEW,
        Arbitrary_Names.LinkedDocumentsSuper,
        null,
        null,
        null,
        true,
    );

    // Legacy: BKT CRN 7436 add security controlled ability to "Sign PDF"
    // documents - CanIPerformArbitraryAction("LinkedDocumentsSignPDF", ...).
    // Shown independent of Supervisor status.
    const isLinkedDocumentsSignPDF = hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.arbitary,
        LABELS_CAPITAL.VIEW,
        Arbitrary_Names.LinkedDocumentsSignPDF,
        null,
        null,
        null,
        true,
    );

    const tableHeaders = [
        { id: 1, key: "documentType",  text: "Document Type"  },
        { id: 2, key: "documentName",  text: "Document Name"  },
        { id: 3, key: "documentDate",  text: "Document Date"  },
        { id: 4, key: "dateAdded",     text: "Date Added"     },
        { id: 5, key: "timeAdded",     text: "Time Added"     },
        { id: 6, key: "author",        text: "Author"         },
        { id: 7, key: "comments",      text: "Comments"       },
    ];

    // Memoized so the array reference only changes when the underlying
    // documents actually change - DataTable resets its row-selection
    // highlight whenever its `data` prop reference changes (see its
    // autoResetSelection effect), so an unmemoized .map() here recreated a
    // new array on every render (e.g. selecting a row) and immediately wiped
    // out the highlight that same click had just applied.
    const tableRows = useMemo(() => linkedDocuments.map((ld, i) => {
    const display   = ld._display ?? {};
    const isPhoto   = ld.photoID && ld.photoID !== 0;
    const doc       = ld.documents?.[0] ?? {};
    const photo     = ld.photos?.[0]    ?? {};

    return {
        _index:          i,
        _raw:            ld,
        documentFileType: display.documentFileType ?? (isPhoto ? "Scanned Photo" : "Linked Documents"),
        documentType:    display.documentType  ?? doc.documentType  ?? "",
        documentName:    display.documentName  ?? doc.documentName  ?? "",
        documentDate:    display.documentDate  ?? doc.documentDate  ?? "",
        dateAdded:       doc.dateAdded         ?? photo.dateAdded   ?? "",
        timeAdded:       doc.timeAdded         ?? photo.timeAdded   ?? "",
        author:          display.author        ?? doc.author        ?? "",
        comments:        display.comments      ?? doc.comments      ?? "",
        documentPath:    display.documentPath  ?? doc.documentPath  ?? "",
    };
    }), [linkedDocuments]);
    
    const hideDocumentModal = () =>{
        setShowModal(false)
        setIsScannedPhoto(false)
        setIsLinkedDocument(false)
        setDocumentMode(null)
    }
    const handleDocumentModal = () =>{
        setCurrentSelectedRow(null)
        setEditingIndex(null)
        setDocumentMode("new")
        setIsConfirmModal(true)
        setShowModal(true);
    }
    
    const handleLinkedDocOrPhoto = (value) =>{
       
        if(value === "Yes"){
            setIsScannedPhoto(false)
            setIsLinkedDocument(true)
        }
        if(value === "No"){
            setIsLinkedDocument(false)
            setIsScannedPhoto(true)
        }
        setIsConfirmModal(false)
    }

    const handleDetailsDocument = () => {
        if (!currentSelectedRow) return;
        setEditingIndex(currentSelectedRow._index);
        setDocumentMode("edit");
        setShowModal(true);
    };

    const handleBreakLink = () => {
        if (!currentSelectedRow) return;
        const updated = linkedDocuments.filter((_, i) => i !== currentSelectedRow._index);
        onChange?.(updated);
        setCurrentSelectedRow(null);
        setEditingIndex(null);
    };

    // Legacy UtilListSupport.vbs DeleteSelectedListItem: the delete confirm
    // popup lists "Label: Value" fields beneath "Are you sure you want to
    // delete this object?" - mirrors Transport's expunge confirm (see
    // pages/Transport/index.jsx buildExpungeConfirmContent).
    const buildBreakLinkConfirmContent = (row) => {
        const fields = [
            { label: "Document Type", value: row?.documentType },
            { label: "Document Name", value: row?.documentName },
            { label: "Document Date", value: row?.documentDate },
            { label: "Date Added", value: row?.dateAdded },
            { label: "Time Added", value: row?.timeAdded },
        ];
        return (
            <>
                Are you sure you want to delete this object?
                <br /><br />
                {fields.map(({ label, value }) => (
                    <span key={label}>
                        <span style={{ display: "inline-block", width: 110 }}>{label}:</span>
                        {value}
                        <br />
                    </span>
                ))}
            </>
        );
    };

    const handleBreakLinkClick = () => {
        if (!currentSelectedRow) return;
        showDialog("confirm", {
            content: buildBreakLinkConfirmContent(currentSelectedRow),
            onConfirm: handleBreakLink,
            onCancel: () => {},
        });
    };

    // Legacy DocumentListInterfaceHelper.asp DisplayLinkedDocument: for a
    // scanned photo (no Document_Path), display the photo itself; for a
    // linked document, open the file. The legacy Windows Shell/ActiveX
    // launch of the associated desktop app has no browser equivalent, so
    // linked documents just open in a new tab.
    // documentFileType (set from _display at save time - see
    // DocumentsDetailsModal's onSaveClick) is the reliable discriminator for
    // both freshly-added, not-yet-backend-persisted rows (whose photoID/
    // documentID are always 0 until the parent record is saved) and rows
    // loaded from the backend.
    const handleDisplayDocument = async () => {
        if (!currentSelectedRow) return;
        const isPhoto = currentSelectedRow.documentFileType === "Scanned Photo";

        if (isPhoto) {
            const fullSizePath = currentSelectedRow._raw?.photos?.[0]?.fullSizePath;
            if (!isNonEmptyVariable(fullSizePath)) {
                notificationService.error("No photo found to display.");
                return;
            }
            window.open(PHOTOS_BASE_URL + getFinalPath(fullSizePath), "_blank");
            return;
        }

        const documentPath = currentSelectedRow._raw?.documents?.[0]?.documentPath;
        if (!isNonEmptyVariable(documentPath)) {
            notificationService.error("No document path found to display.");
            return;
        }

        // A plain window.open to the document server can't carry the app's
        // auth token, so fetch it through the authenticated api client
        // instead (bypasses whatever blocked the direct link), then either
        // preview it (PDF/image the browser can render) or save it locally
        // (doc/xls/etc, which the browser can't render inline) - the
        // browser-vs-external-app split legacy did with Windows Shell.
        try {
            const response = await api.get(
                DOCUMENTS_BASE_URL + getFinalPath(documentPath),
                { responseType: "blob" },
            );
            const blobUrl = URL.createObjectURL(response.data);
            const fileName = documentPath.split(/[\\/]/).pop();
            const extension = fileName?.split(".")?.pop()?.toLowerCase();

            if (["pdf", "jpg", "jpeg", "png", "htm", "html"].includes(extension)) {
                window.open(blobUrl, "_blank");
            } else {
                const link = window.document.createElement("a");
                link.href = blobUrl;
                link.download = fileName || "document";
                window.document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch {
            notificationService.error("Unable to download the document.");
        }
    };

    // Legacy DocumentListInterfaceHelper.asp SignLinkedDocument: only PDFs
    // can be signed (DocString(0) is overridden to " *.pdf" before the
    // extension check) - anything else shows "Files of type *.<ext> cannot
    // be signed.". Actual signing (legacy used the pDocSigner ActiveX
    // control, which has no browser equivalent) is not implemented yet.
    const handleSignPDF = () => {
        if (!currentSelectedRow) return;
        const isPhoto = currentSelectedRow.documentFileType === "Scanned Photo";
        const path = isPhoto
            ? currentSelectedRow._raw?.photos?.[0]?.fullSizePath
            : currentSelectedRow._raw?.documents?.[0]?.documentPath;
        const extension = isNonEmptyVariable(path)
            ? path.split(".").pop()?.toLowerCase()
            : "";

        if (extension !== "pdf") {
            showDialog("info", {
                title: "Sign PDF",
                content: `Files of type *.${extension || "unknown"} cannot be signed.`,
                confirmButtonText: "Ok",
            });
            return;
        }
        // TODO: implement PDF signing
    };

    const handleRowClick = (rowData, index) => {
        // console.log("Row Click:", rowData);
        if(rowData) {
            if(rowData?.documentFileType == "Scanned Photo"){
                setIsLinkedDocument(false)
                setIsScannedPhoto(true)
            }
            if(rowData?.documentFileType == "Linked Documents"){
                setIsScannedPhoto(false)
                setIsLinkedDocument(true)
            }
            setCurrentSelectedRow(rowData);
            if(index !== undefined && index !== null) {
                setEditingIndex(rowData._index);
                setDocumentMode("edit");
                setShowModal(true);
            }
        }
    };

    return(
        <>

        <StyledGridRow justify="end">
            <StyledGridCol marginBottom="10px">
               <StyledFlex gap="10px">
               <Button onClick={handleDocumentModal} disabled={!bookingsFieldsEnabled}> <PlusOutlined/> Add Link</Button>
                <Button onClick={handleDetailsDocument} disabled={!currentSelectedRow}>
                    {!bookingsFieldsEnabled ? "Details" : "Modify"}
                </Button>
                {
                    isLinkedDocumentsSuper && (
                        <Button onClick={handleBreakLinkClick} disabled={!currentSelectedRow || !bookingsFieldsEnabled}>
                            Break Link
                        </Button>
                    )
                }
                <Button onClick={handleDisplayDocument} disabled={!currentSelectedRow}>
                    Display Document
                </Button>
                {
                    isLinkedDocumentsSignPDF && (
                        <Button onClick={handleSignPDF} disabled={!currentSelectedRow}>
                            Sign PDF
                        </Button>
                    )
                }
               </StyledFlex>
            </StyledGridCol>
        </StyledGridRow>

        <DataTable
            headers={tableHeaders}
            data={tableRows}
            rowUniqueId="_index"
            onRowClick={handleRowClick}
            selectionColumnVisible={false}
            allowTableSorting={false}
            pagination={null}
        />

        {/* isConfirmModal */}
        
       
        { showModal && (
            <DocumentsDetailsModal
                show={showModal}
                onHide={hideDocumentModal}
                documentMode={documentMode}
                data={currentSelectedRow}
                documentFileType={
                    isLinkedDocument ? "Linked Documents" :
                    isScannedPhoto   ? "Scanned Photo"    : ""
                }
                editingIndex={editingIndex}
                documents={linkedDocuments}
                onChange={onChange}
                bookingsFieldsEnabled={bookingsFieldsEnabled}
                idFieldName={idFieldName}
                parentIdFieldName={parentIdFieldName}
                parentId={parentId}
                isSearchMode={isSearchMode}
            />
        )}

        { isConfirmModal && (
            <Modal
            header="Linked Document or Scanned Photo?"
            size="md"
            designPattern="ant"
            show={isConfirmModal}
            footerActions={[
                <Button key="submit" type="primary" onClick={()=>handleLinkedDocOrPhoto("Yes")}>Yes</Button>,
                <Button key="back" onClick={()=>handleLinkedDocOrPhoto("No")}>No</Button>,
            ]}>
                <p>Is this a Linked Document?</p>
            </Modal>
        )}


        </>
    )
}
export default LinkedScannedDocuments;